// LeitstellePage.jsx — Leitstelle am Turniertag (/leitstelle/:turnierId, 18.10.2026)
//
// Eine Seite für alle Matten: was läuft, was kommt, wer ist verbunden, wo
// klemmt es. Gelesen wird GET /api/leitstelle/:id (services/leitstelle.js)
// alle 5 Sekunden; die Ampel rechnet der Server (utils/leitstelleAmpel.js).
// Zugang: Admin des Turniers oder Turnierleitung (middleware/darfLeitstelle).
//
// Kein Rechnen hier: Verzug, Zustand und Ampel kommen fertig vom Server, damit
// zwei Leitstellen-Bildschirme nie Verschiedenes zeigen.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import './LeitstellePage.css';
import { statusText } from '../lib/turnierStatus';

const TAKT_MS = 5000;
const VERALTET_MS = 15000;

const ART = { kampf: 'Kampf', formen: 'Formen', sv: 'Selbstverteidigung', bruchtest: 'Bruchtest' };
const ZUSTAND = { offen: 'offen', laeuft: 'läuft', fertig: 'fertig' };
const FARBE = { rot: 'Achtung', gelb: 'Prüfen', gruen: 'In Ordnung', grau: 'Ruhe' };

const uhr = (d) => d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

function Kopfzahl({ titel, wert, sub, ton, to, href }) {
  const inhalt = (
    <>
      <span className="lst-kz-titel">{titel}</span>
      <span className="lst-kz-wert">{wert}</span>
      {sub && <span className="lst-kz-sub">{sub}</span>}
    </>
  );
  const cls = `lst-kz${ton ? ` lst-kz--${ton}` : ''}`;
  if (href) return <a className={cls} href={href} target="_blank" rel="noreferrer">{inhalt}</a>;
  if (to) return <Link className={cls} to={to}>{inhalt}</Link>;
  return <div className={cls}>{inhalt}</div>;
}

function Kategorie({ k, beschriftung }) {
  if (!k) return null;
  const f = k.fortschritt;
  const prozent = f && f.gesamt > 0 ? Math.round((f.fertig / f.gesamt) * 100) : null;
  return (
    <div className="lst-kat">
      <div className="lst-kat-kopf">
        <span className="lst-kat-label">{beschriftung}</span>
        <span className={`lst-badge lst-badge--${k.art}`}>{ART[k.art] || k.art}</span>
        <span className={`lst-zustand lst-zustand--${k.zustand}`}>{ZUSTAND[k.zustand] || k.zustand}</span>
      </div>
      <div className="lst-kat-name" title={k.division_code}>{k.name}</div>
      <div className="lst-kat-meta">
        <span>{k.division_code}</span>
        {k.start_time && <span>Plan {k.start_time_plan || k.start_time}{k.start_time_plan && k.start_time !== k.start_time_plan ? ` · jetzt ${k.start_time}` : ''}</span>}
        {k.verzug != null && k.verzug !== 0 && (
          <span className={k.verzug >= 15 ? 'lst-rot' : k.verzug >= 5 ? 'lst-gelb' : ''}>
            {k.verzug > 0 ? `${k.verzug} min Verzug` : `${-k.verzug} min früher`}
          </span>
        )}
      </div>
      {prozent !== null && (
        <div className="lst-balken" title={`${f.fertig} von ${f.gesamt}`}>
          <span style={{ width: `${prozent}%` }} />
          <em>{f.fertig}/{f.gesamt} {k.art === 'kampf' ? 'Kämpfe' : 'Starter'}</em>
        </div>
      )}
    </div>
  );
}

function Matte({ m, turnierId, istAdmin, hallenSchluessel }) {
  const s = m.starter;
  const kategorieZiel = m.aktuell?.art === 'formen' || m.aktuell?.art === 'sv' ? '/dashboard/formen'
    : m.aktuell?.art === 'bruchtest' ? '/dashboard/bruchtest' : '/dashboard/brackets';
  return (
    <section className={`lst-matte lst-matte--${m.ampel.farbe}`} aria-label={`${m.name}: ${FARBE[m.ampel.farbe]}`}>
      <header className="lst-matte-kopf">
        <span className={`lst-ampel lst-ampel--${m.ampel.farbe}`} aria-hidden="true" />
        <h2>{m.name}</h2>
        <span className="lst-matte-farbe">{FARBE[m.ampel.farbe]}</span>
        <span className="lst-matte-rest" title="Kategorien auf dieser Matte, die noch nicht fertig sind">{m.rest} von {m.geplant} offen</span>
      </header>

      {m.ampel.gruende.length > 0 && m.ampel.farbe !== 'grau' && (
        <ul className="lst-gruende">{m.ampel.gruende.map((g) => <li key={g}>{g}</li>)}</ul>
      )}

      {m.aktuell ? <Kategorie k={m.aktuell} beschriftung="Jetzt" /> : <div className="lst-leer">Keine Kategorie an der Matte</div>}

      {s && (s.rot || s.blau) && (
        <div className="lst-starter">
          <span className="lst-rotseite">{s.rot || 'Rot'}</span>
          {s.punkte && <b>{s.punkte.rot} : {s.punkte.blau}</b>}
          <span className="lst-blauseite">{s.blau || 'Blau'}</span>
          {s.restzeit != null && <span className="lst-zeit">{Math.floor(s.restzeit / 60)}:{String(s.restzeit % 60).padStart(2, '0')}{s.laeuft ? '' : ' (angehalten)'}</span>}
        </div>
      )}
      {s && s.formen && (
        <div className="lst-starter lst-starter--formen">
          <span>{s.formen_status === 'active' ? 'Auf der Fläche' : 'Als Nächstes'}: <b>{s.formen}</b></span>
          {s.danach && <span className="lst-leise">danach {s.danach}</span>}
        </div>
      )}
      {m.tablet_hinweis && <div className="lst-fehlerzeile" role="alert">{m.tablet_hinweis}</div>}

      {m.naechste && <Kategorie k={m.naechste} beschriftung="Danach" />}

      <div className="lst-geraete">
        <span className={m.tablets.center.length ? 'lst-ok' : 'lst-fehlt'} title={m.tablets.center.join(', ')}>
          Center {m.tablets.center.length ? m.tablets.center.join(', ') : 'fehlt'}
        </span>
        <span className={m.tablets.judges.length ? 'lst-ok' : 'lst-fehlt'} title={m.tablets.judges.join(', ')}>
          Judges {m.tablets.judges.length}
        </span>
        <span className={m.tablets.anzeigen ? 'lst-ok' : 'lst-fehlt'}>Mattenschirm {m.tablets.anzeigen}</span>
        {m.aufruf && <span className="lst-ok" title={[m.aufruf.division_code, m.aufruf.label].filter(Boolean).join(' · ')}>Aufgerufen {m.aufruf.name || m.aufruf.division_code}</span>}
      </div>

      <nav className="lst-links">
        {/* Mit Hallenschlüssel zeigt der Mattenschirm Fotos (21.10.2026) */}
        <a href={`/display/${m.nummer}?t=${turnierId}${hallenSchluessel ? `&k=${hallenSchluessel}` : ''}`} target="_blank" rel="noreferrer">Mattenschirm</a>
        <a href={`/judge/${m.nummer}`} target="_blank" rel="noreferrer">Kampfrichter</a>
        <a href={`/aufruf-control/${turnierId}`} target="_blank" rel="noreferrer">Aufruf</a>
        {istAdmin && <Link to={kategorieZiel}>Kategorie</Link>}
        {istAdmin && <Link to="/dashboard/zeitplan">Zeitplan</Link>}
      </nav>
    </section>
  );
}

export default function LeitstellePage() {
  const { turnierId } = useParams();
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState('');
  const [stand, setStand] = useState(null);
  const [jetzt, setJetzt] = useState(Date.now());
  const laeuft = useRef(false);

  const laden = useCallback(async () => {
    if (laeuft.current) return;
    laeuft.current = true;
    try {
      const r = await fetch(`/api/leitstelle/${turnierId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        cache: 'no-store',
      });
      const d = await r.json().catch(() => ({}));
      if (r.status === 401) throw new Error('Nicht angemeldet. Bitte unter /anmelden neu anmelden.');
      if (r.status === 403) throw new Error(d.error || 'Kein Zugang zur Leitstelle dieses Turniers.');
      if (!r.ok || !d.success) throw new Error(d.error || `Fehler ${r.status}`);
      setDaten(d);
      setStand(new Date());
      setFehler('');
    } catch (e) {
      setFehler(e.message === 'Failed to fetch' ? 'Server nicht erreichbar.' : e.message);
    } finally {
      laeuft.current = false;
    }
  }, [turnierId]);

  useEffect(() => {
    laden();
    const t = setInterval(laden, TAKT_MS);
    const u = setInterval(() => setJetzt(Date.now()), 1000);
    return () => { clearInterval(t); clearInterval(u); };
  }, [laden]);

  // Hallenschlüssel (21.10.2026): EIN Schlüssel für alle Hallenbildschirme.
  // Steckt im Link der Mattenschirme, Leinwände (Formen, Sieger, Bruchtest,
  // Rumble) und der Siegerehrung; ohne ihn laufen die Leinwände nicht und
  // nirgends erscheinen Fotos. Erneuern macht alle alten Links ungültig
  // (GET/POST /api/leitstelle/:id/hallenschluessel; das Lesen legt ihn an).
  const [hs, setHs] = useState(null);
  const [hsLaeuft, setHsLaeuft] = useState(false);
  useEffect(() => {
    let ab = false;
    fetch(`/api/leitstelle/${turnierId}/hallenschluessel`, { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }, cache: 'no-store' })
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok || !d.success) throw new Error(d.error || `Fehler ${r.status}`); return d; })
      .then((d) => { if (!ab) setHs(d); })
      .catch((e) => { if (!ab) setHs({ fehler: e.message }); });
    return () => { ab = true; };
  }, [turnierId]);
  const hallenSchluesselErneuern = async () => {
    if (hs?.schluessel && !window.confirm('Hallenschlüssel erneuern?\n\nAlle alten Links der Hallenbildschirme werden ungültig: Formen-, Sieger-, Bruchtest- und Rumble-Leinwand zeigen nichts mehr, Mattenschirme und Siegerehrung keine Fotos mehr, bis sie mit dem neuen Link geöffnet werden (Gerätekarte, Panel „Leinwand öffnen“, Links hier).')) return;
    setHsLaeuft(true);
    try {
      const r = await fetch(`/api/leitstelle/${turnierId}/hallenschluessel`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) throw new Error(d.error || `Fehler ${r.status}`);
      setHs(d);
    } catch (e) {
      setFehler(`Hallenschlüssel nicht erzeugt: ${e.message}`);
    } finally {
      setHsLaeuft(false);
    }
  };

  const erledigen = async (id) => {
    try {
      const r = await fetch(`/api/leitstelle/${turnierId}/ereignisse/${id}/erledigt`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) throw new Error(d.error || `Fehler ${r.status}`);
      laden();
    } catch (e) {
      setFehler(`Abhaken fehlgeschlagen: ${e.message}`);
    }
  };

  // Stichkampf ansetzen (19.10.2026): derselbe Weg wie im Bracket
  // (POST /api/brackets/:bracketId/stichkampf). Recht prüft der Server:
  // Turnierleitung dieses Turniers oder Admin im eigenen Verband.
  const [stichLaeuft, setStichLaeuft] = useState(null);
  const [stichMeldung, setStichMeldung] = useState('');
  const stichkampfAnsetzen = async (g) => {
    const wer = g.gruppen.map((gr) => gr.map((p) => p.name).join(', ')).join(' / ');
    if (!window.confirm(`Stichkampf ansetzen für ${g.name}?\n\nGleichstand: ${wer}`)) return;
    setStichLaeuft(g.bracket_id);
    setStichMeldung('');
    try {
      const r = await fetch(`/api/brackets/${g.bracket_id}/stichkampf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) throw new Error(d.error || d.message || `Fehler ${r.status}`);
      const anzahl = d.data?.kaempfe?.length || 0;
      setStichMeldung(`${g.name}: ${anzahl} Stichkampf${anzahl === 1 ? '' : '-Kämpfe'} angesetzt, auf derselben Matte.`);
      laden();
    } catch (e) {
      setFehler(`Stichkampf nicht angesetzt: ${e.message}`);
    } finally {
      setStichLaeuft(null);
    }
  };

  const veraltet = !stand || jetzt - stand.getTime() > VERALTET_MS;
  const istAdmin = daten?.rolle === 'admin';
  const k = daten?.kopf;
  const rot = daten ? daten.matten.filter((m) => m.ampel.farbe === 'rot').length : 0;

  return (
    <div className="lst">
      <header className="lst-kopf">
        <div>
          <h1>Leitstelle</h1>
          <div className="lst-turnier">{daten?.turnier?.name || 'Turnier wird geladen …'}{daten?.turnier?.status ? ` · ${statusText(daten.turnier.status)}` : ''}</div>
        </div>
        <div className={`lst-stand${veraltet ? ' lst-stand--alt' : ''}`} role="status">
          {stand ? `Stand ${uhr(stand)}` : 'Noch kein Stand'}
          {veraltet && stand && <span> · nicht aktuell</span>}
        </div>
        <nav className="lst-kopf-links">
          {istAdmin && <Link to={`/turniertag/${turnierId}`}>Turniertag vorbereiten</Link>}
          {istAdmin && <Link to="/dashboard">Dashboard</Link>}
          {!istAdmin && daten && <Link to="/staff">Mein Einsatz</Link>}
        </nav>
      </header>

      {fehler && <div className="lst-banner" role="alert">{fehler}</div>}

      {k && (
        <div className="lst-kopfzahlen">
          <Kopfzahl titel="Eingecheckt" wert={`${k.checkin.eingecheckt} / ${k.checkin.erwartet}`}
            sub={`${Math.max(0, k.checkin.erwartet - k.checkin.eingecheckt)} fehlen`} to={istAdmin ? '/dashboard/checkin' : null} />
          <Kopfzahl titel="Waage offen" wert={k.waage.ausstehend}
            sub={k.waage.nicht_bestanden ? `${k.waage.nicht_bestanden} nicht bestanden` : `von ${k.waage.gesamt}`}
            ton={k.waage.nicht_bestanden ? 'rot' : null} to={istAdmin ? '/dashboard/einwaage' : null} />
          <Kopfzahl titel="Einzelstarter" wert={k.einzelstarter.anzahl} sub={(k.einzelstarter.namen || k.einzelstarter.codes).slice(0, 3).join(', ') || 'keine'}
            ton={k.einzelstarter.anzahl ? 'gelb' : null} to={istAdmin ? '/dashboard/einzelstarter' : null} />
          <Kopfzahl titel="Kategorien fertig" wert={`${k.kategorien.fertig} / ${k.kategorien.gesamt}`} to={istAdmin ? '/dashboard/zeitplan' : null} />
          <Kopfzahl titel="Siegerehrung bereit" wert={k.siegerehrung.bereit}
            sub={k.siegerehrung.liste.slice(0, 2).map((x) => x.name || x.division_code).join(', ') || 'keine wartet'}
            ton={k.siegerehrung.bereit ? 'gelb' : null} to={istAdmin ? '/dashboard/siegerehrung' : null} />
          <Kopfzahl titel="Ansage-Station"
            wert={!k.ansage.eingeschaltet ? 'aus' : k.ansage.verbunden ? 'verbunden' : 'getrennt'}
            sub={k.ansage.zuletzt_vor_sekunden != null ? `zuletzt vor ${k.ansage.zuletzt_vor_sekunden} s` : 'noch nie gemeldet'}
            ton={k.ansage.eingeschaltet && !k.ansage.verbunden ? 'rot' : null} href={`/ansage/${turnierId}`} />
          <Kopfzahl titel="Hallenschirme" wert={k.hallenschirme} sub="fragen gerade nach"
            ton={k.hallenschirme ? null : 'gelb'} href={`/aufruf/${turnierId}`} />
          {k.nachmeldungen_offen > 0 && (
            <Kopfzahl titel="Nachmeldung ohne Liste" wert={k.nachmeldungen_offen} sub="siehe Meldungen" ton="rot" />
          )}
        </div>
      )}

      {daten && (
        <section className="lst-hs" aria-label="Hallenschlüssel">
          <div>
            <b>Hallenschlüssel</b>
            <span className="lst-leise">
              {hs?.fehler ? `Hallenschlüssel nicht ladbar: ${hs.fehler}`
                : hs?.schluessel ? `Aktiv${hs.erstellt_am ? ` seit ${hs.erstellt_am}` : ''}. Gilt für alle Bildschirme in der Halle: Mattenschirme, Leinwände und Siegerehrung. Nur mit diesem Schlüssel im Link laufen die Leinwände und erscheinen Fotos (bei Einwilligung).`
                : 'Kein Hallenschlüssel: Leinwände laufen nicht, Bildschirme zeigen Vereinslogos statt Fotos.'}
            </span>
          </div>
          <button type="button" onClick={hallenSchluesselErneuern} disabled={hsLaeuft || !hs || !!hs.fehler} data-hallenschluessel={hs?.schluessel ? 'aktiv' : 'fehlt'}>
            {hs?.schluessel ? 'Erneuern' : 'Hallenschlüssel erzeugen'}
          </button>
        </section>
      )}

      {daten && (
        <div className="lst-haupt">
          <div className="lst-matten">
            {daten.matten.length === 0 && <div className="lst-leer">Für dieses Turnier sind keine Matten angelegt.</div>}
            {daten.matten.map((m) => <Matte key={m.kampfflaeche_id} m={m} turnierId={turnierId} istAdmin={istAdmin} hallenSchluessel={hs?.schluessel || null} />)}
          </div>

          <aside className="lst-meldungen" aria-label="Meldungen">
            {(daten.gleichstaende?.length > 0 || stichMeldung) && (
              <section className="lst-gleichstand" aria-label="Gleichstand">
                <h2>Gleichstand · Stichkampf nötig</h2>
                {stichMeldung && <div className="lst-leise" role="status">{stichMeldung}</div>}
                <ul>
                  {(daten.gleichstaende || []).map((g) => (
                    <li key={g.bracket_id} className="lst-meldung lst-meldung--warnung">
                      <div className="lst-meldung-kopf">
                        <span>Pool {g.bracket_id}</span>
                        {g.division_code && <span>{g.division_code}</span>}
                      </div>
                      <div className="lst-meldung-text">
                        <b>{g.name}</b>: gleich nach Siegen, direktem Vergleich und Punkten
                        {g.gruppen.map((gr, i) => <div key={i}>{gr.map((p) => p.name).join(', ')}</div>)}
                      </div>
                      <button type="button" disabled={stichLaeuft === g.bracket_id} onClick={() => stichkampfAnsetzen(g)}>
                        {stichLaeuft === g.bracket_id ? 'Wird angesetzt …' : 'Stichkampf ansetzen'}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <h2>Meldungen{rot ? ` · ${rot} ${rot === 1 ? 'Matte' : 'Matten'} rot` : ''}</h2>
            {daten.ereignisse.length === 0 && <div className="lst-leise">Keine Meldungen. Fehler beim Speichern von der Matte erscheinen hier.</div>}
            <ul>
              {daten.ereignisse.map((e) => (
                <li key={e.id} className={`lst-meldung lst-meldung--${e.stufe}${e.erledigt ? ' lst-meldung--erledigt' : ''}`}>
                  <div className="lst-meldung-kopf">
                    <span>{e.uhrzeit}</span>
                    {e.matte && <span>Matte {e.matte}</span>}
                    {e.division_code && <span title={e.division_code}>{e.kategorie_name || e.division_code}</span>}
                  </div>
                  <div className="lst-meldung-text">{e.text}</div>
                  {e.quelle && <div className="lst-leise">{e.quelle}</div>}
                  {e.erledigt
                    ? <div className="lst-leise">erledigt{e.erledigt_von ? ` von ${e.erledigt_von}` : ''}</div>
                    : <button type="button" onClick={() => erledigen(e.id)}>Erledigt</button>}
                </li>
              ))}
            </ul>
          </aside>
        </div>
      )}
    </div>
  );
}
